import { alpha, Avatar, Box, Chip, Drawer, Grid, Paper, Stack, Typography } from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { BasePathProvider } from 'components/Providers'
import MyLogsList from './List/List'
import MyLogsFormIndex from './Form/index'
import { useEffect } from 'react'
import { search } from 'store/mentorship/mentorship'

const basePath = '/mentorship/my-logs'

const StatCard = ({ label, value, color }) => (
  <Paper
    elevation={0}
    sx={{
      px: 2,
      pt: 2,
      pb: 2.5,
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      borderTop: `3px solid ${color}`,
      textAlign: 'center',
    }}
  >
    <Typography variant="h4" fontWeight={800} lineHeight={1} sx={{ color }}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
      {label}
    </Typography>
  </Paper>
)

export default function MyLogsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const personId = useSelector(state => state.session.person?.id);
  const mentorships = useSelector(state => state.mentorship.mentorship.list);
  const mentorship = mentorships?.find(m => m.menteeId === personId);
  const logList = useSelector(state => state.mentorship.log.list);

  useEffect(() => {
    if (!mentorships) dispatch(search())
  }, [dispatch, Boolean(mentorships)])

  const total = logList?.length ?? '—'
  const submitted = logList?.filter(l => l.status === 'submitted').length ?? '—'
  const approved = logList?.filter(l => l.status === 'approved').length ?? '—'
  const rejected = logList?.filter(l => l.status === 'rejected').length ?? '—'

  const mentorName = mentorship?.mentor
    ? `${mentorship.mentor.firstName} ${mentorship.mentor.lastName}`
    : null

  return (
    <BasePathProvider value={basePath}>
      <Box sx={{ p: 1 }}>
        {mentorName && (
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Chip
              avatar={
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <PersonIcon sx={{ fontSize: 16, color: '#fff' }} />
                </Avatar>
              }
              label={`${t('Mentor')}: ${mentorName}`}
              variant="outlined"
              color="primary"
              sx={{ height: 36, fontSize: 13, fontWeight: 600 }}
            />
          </Stack>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <StatCard label={t('Total Logs')} value={total} color="#1976d2" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard label={t('Pending Review')} value={submitted} color="#ed6c02" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard label={t('Approved')} value={approved} color="#2e7d32" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard label={t('Rejected')} value={rejected} color="#d32f2f" />
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          onClick={() => mentorship && navigate(`${basePath}/new`)}
          sx={{
            mb: 2,
            p: 1.75,
            borderRadius: 2,
            border: '1.5px dashed',
            borderColor: mentorship ? 'primary.main' : 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            cursor: mentorship ? 'pointer' : 'default',
            color: mentorship ? 'primary.main' : 'text.disabled',
            transition: 'background 0.15s',
            '&:hover': mentorship ? {
              bgcolor: theme => alpha(theme.palette.primary.main, 0.06),
            } : {},
          }}
        >
          <AddCircleOutlineIcon fontSize="small" />
          <Typography variant="body2" fontWeight={600}>
            {t('Add New Log Entry')}
          </Typography>
        </Paper>

        <MyLogsList mentorshipId={mentorship?.id} />
      </Box>

      <Drawer
        PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}
        open={Boolean(id)}
        anchor="right"
        onClose={() => navigate(basePath)}
      >
        <MyLogsFormIndex mentorshipId={mentorship?.id} />
      </Drawer>
    </BasePathProvider>
  )
}
